import { useParams } from 'react-router-dom'
import { useState } from 'react'

import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import HomeWorkIcon from '@mui/icons-material/HomeWork'

import PropertyHeader from './PropertyHeader'
import PropertyInfoCard from './PropertyInfoCard'
import PropertyCurrentCard from './PropertyCurrentCard'
import PropertyHistoryCard from './PropertyHistoryCard'
import PropertyDocumentsCard from './PropertyDocumentsCard'
import PhotoCarousel from './PhotoCarousel'
import PropertySection, { SectionTitle } from './PropertySection'
import IndexCard from '../Dashboard/IndexCard'
import PaymentCard from '../Dashboard/PaymentCard'


/**
 * Property page — shows all information about a single property.
 *
 * ── Information architecture ────────────────────────────────────────────
 *
 * The previous version interleaved two different subjects — the physical
 * property (address, DPE, owners, photos) and the current tenancy (lease,
 * rent, payments, indexations) — across three competing columns. Nothing
 * signalled which column to read first, and the two subjects bled into
 * each other (tenants, a lease concept, lived inside the "property info"
 * card; documents that are really lease paperwork sat next to photos of
 * the building).
 *
 * This version makes the split explicit and gives each subject its own
 * place:
 *
 *   PropertyHeader  — always visible, never behind a tab. Answers "which
 *                      property, where, what DPE grade, whose". A single
 *                      row, so it orients without competing for space.
 *
 *   Tab "Bien"       — everything about the property itself: full address,
 *                       type, ownership split, DPE detail, photos. This
 *                       changes rarely. It sits on the left because it
 *                       reads as the more fundamental, "about the place"
 *                       subject — but it isn't the default tab.
 *
 *   Tab "Location"   — everything about the current tenancy: pending
 *                       actions (indexation / payment due), the active
 *                       lease with its tenants and rent, payment and
 *                       indexation history, lease-related documents. This
 *                       is what a manager checks day to day, so it opens
 *                       selected even though it's on the right.
 *
 * DECISION: tab selection is keyed by name ('location' / 'bien'), not by
 * index. Left-to-right order and "which one opens by default" are two
 * separate decisions — coupling them to the same integer is what would
 * make reordering the tabs risk silently changing the default. Named
 * values keep the two independent and self-documenting at the call site.
 *
 * DECISION: IndexCard and PaymentCard are shared with the dashboard and
 * are rendered unmodified. They sit at the top of "Location" under a plain
 * text label rather than inside another bordered PropertySection — they
 * already look like self-contained cards, and boxing a card inside a box
 * was exactly the kind of clutter this redesign set out to remove.
 */
export default function Property({ supabase }) {

    const { propertyId } = useParams()
    const [tab, setTab] = useState('location')

    return (
        <Box>

            <PropertyHeader supabase={supabase} propertyId={propertyId} />

            <Tabs
                value={tab}
                onChange={(_, newValue) => setTab(newValue)}
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab
                    label="Bien"
                    value="bien"
                    icon={<HomeWorkIcon fontSize="small" />}
                    iconPosition="start"
                    id="property-tab-bien"
                    aria-controls="property-tabpanel-bien"
                    sx={{ minHeight: 48, fontWeight: 600 }}
                />
                <Tab
                    label="Location"
                    value="location"
                    icon={<VpnKeyIcon fontSize="small" />}
                    iconPosition="start"
                    id="property-tab-location"
                    aria-controls="property-tabpanel-location"
                    sx={{ minHeight: 48, fontWeight: 600 }}
                />
            </Tabs>

            {/* ── Tab : Bien ────────────────────────────────────────────────── */}
            <Box role="tabpanel" hidden={tab !== 'bien'} id="property-tabpanel-bien" aria-labelledby="property-tab-bien">
                {tab === 'bien' && (
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 7 }}>
                            <PropertyInfoCard supabase={supabase} propertyId={propertyId} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <PropertySection title="Photos">
                                <PhotoCarousel height="280px" />
                            </PropertySection>
                        </Grid>
                    </Grid>
                )}
            </Box>

            {/* ── Tab : Location ────────────────────────────────────────────── */}
            <Box role="tabpanel" hidden={tab !== 'location'} id="property-tabpanel-location" aria-labelledby="property-tab-location">
                {tab === 'location' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                        <Box>
                            <SectionTitle sx={{ mb: 2, display: 'block' }}>À traiter</SectionTitle>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <IndexCard supabase={supabase} propertyId={propertyId} />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <PaymentCard supabase={supabase} propertyId={propertyId} />
                                </Grid>
                            </Grid>
                        </Box>

                        <PropertyCurrentCard supabase={supabase} propertyId={propertyId} />
                        <PropertyHistoryCard supabase={supabase} propertyId={propertyId} />
                        <PropertyDocumentsCard />

                    </Box>
                )}
            </Box>

        </Box>
    )
}